import { osnovaApi } from '../base';
import { parseResponse } from '../parseResponse';
import type { AppDispatch } from '@app/store';
import type {
  GeoAction,
  GeoColumnOrder,
  GeoDesignElement,
  GeoNotesAssist,
  GeoObjects,
  GeoOverride,
  GeoScheme,
  GeoStampAssist,
  GeoStats,
  GeoSurveyPoint,
} from '@entities/geodesy/types';
import type {
  GeoAssembleBody,
  GeoAutoBindBody,
  GeoAxesBindBody,
  GeoAxisGrids,
  GeoCodeRuleInput,
  GeoDesignExportFormat,
  GeoExtractBody,
  GeoInfo,
  GeoIsoNodes,
  GeoSchemeCreateBody,
  GeoSchemeListParams,
  GeoSchemeListResponse,
  GeoRdDocuments,
  GeoSchemePatchBody,
  GeoSheetAxes,
  GeoSheetPatchBody,
  GeoSheetCoords,
  GeoSheetStructure,
  GeoSnapsResponse,
  GeoStructureBindBody,
  GeoSurveyAssist,
  GeoSurveyUploadMode,
  GeoTolerancesResponse,
  GeoVersionFileKind,
} from '@entities/geodesy/model';

/**
 * ОСНОВА.Геодезист (`/api/v1/agents/geodesy`, ТЗ AI-GEO-2026 §8.1).
 *
 * Почти все мутации возвращают свежую карточку ИС целиком — кладём её прямо в
 * кеш `getGeoScheme` (без повторного GET) и инвалидируем реестр/счётчики по тегу
 * `GeoSchemes`. Байтовые ответы (превью листа, PNG/DXF/PDF версий) — Blob;
 * ошибку при этом разбираем как JSON, чтобы показать `message`, а не «[object Blob]».
 *
 * ИИ-помощник (`assist/*`) ничего не сохраняет — возвращает предложения; в схему
 * они попадают только обычными мутациями после того, как человек их принял.
 */

const BASE = '/agents/geodesy';

async function blobOrError(response: Response): Promise<unknown> {
  if (!response.ok) return parseResponse(response);
  return response.blob();
}

function form(fields: Record<string, string | Blob>): FormData {
  const fd = new FormData();
  for (const [key, value] of Object.entries(fields)) fd.append(key, value);
  return fd;
}

/** Ответ мутации — карточка ИС: сразу в кеш карточки. */
async function storeScheme(
  dispatch: AppDispatch,
  queryFulfilled: Promise<{ data: GeoScheme }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    void dispatch(geodesyApi.util.upsertQueryData('getGeoScheme', data.id, data));
  } catch {
    /* ошибку показывает экран через unwrap() */
  }
}

export const geodesyApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /* ── Справочники и счётчики ─────────────────────────────────────────── */

    getGeoInfo: b.query<GeoInfo, void>({
      query: () => `${BASE}/info`,
      providesTags: ['Geo'],
    }),
    /** Проекты ПТО и площадки для пикеров объекта. */
    getGeoObjects: b.query<GeoObjects, void>({
      query: () => `${BASE}/objects`,
      providesTags: ['Geo'],
    }),
    /** Типы работ с допусками по СП (значение + норматив). */
    getGeoTolerances: b.query<GeoTolerancesResponse, void>({
      query: () => `${BASE}/tolerances`,
      providesTags: ['Geo'],
    }),
    /** Счётчики для бейджей. Зависят и от ИС, и от входящих заявок. */
    getGeoStats: b.query<GeoStats, void>({
      query: () => `${BASE}/stats`,
      providesTags: ['GeoSchemes', 'Requests'],
    }),

    /* ── Реестр и карточка ИС ───────────────────────────────────────────── */

    listGeoSchemes: b.query<GeoSchemeListResponse, GeoSchemeListParams>({
      query: (p) => ({
        url: `${BASE}/schemes`,
        params: {
          ...(p.status ? { status: p.status } : {}),
          ...(p.work_type ? { work_type: p.work_type } : {}),
          ...(p.project_id ? { project_id: p.project_id } : {}),
          ...(p.site_id ? { site_id: p.site_id } : {}),
          ...(p.element_id ? { element_id: p.element_id } : {}),
          ...(p.q && p.q.trim() ? { q: p.q.trim() } : {}),
          ...(p.limit != null ? { limit: p.limit } : {}),
          ...(p.offset ? { offset: p.offset } : {}),
        },
      }),
      providesTags: ['GeoSchemes'],
    }),
    createGeoScheme: b.mutation<GeoScheme, GeoSchemeCreateBody>({
      query: (body) => ({ url: `${BASE}/schemes`, method: 'POST', body }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Создать ИС по заявке: backend принимает заявку и переносит объект/площадку. */
    createGeoSchemeFromRequest: b.mutation<GeoScheme, string>({
      query: (requestId) => ({
        url: `${BASE}/schemes/from-request/${requestId}`,
        method: 'POST',
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes', 'Requests'],
    }),
    getGeoScheme: b.query<GeoScheme, string>({
      query: (id) => `${BASE}/schemes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'GeoScheme', id }],
    }),
    updateGeoScheme: b.mutation<GeoScheme, { id: string } & GeoSchemePatchBody>({
      query: ({ id, ...body }) => ({ url: `${BASE}/schemes/${id}`, method: 'PATCH', body }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    deleteGeoScheme: b.mutation<{ ok: true }, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Раскладка зон по умолчанию для текущего формата листа. */
    resetGeoLayout: b.mutation<GeoScheme, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/layout/reset`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),

    /* ── Проектный лист (подложка) ──────────────────────────────────────── */

    uploadGeoSheet: b.mutation<GeoScheme, { id: string; file: File }>({
      query: ({ id, file }) => ({
        url: `${BASE}/schemes/${id}/sheet`,
        method: 'POST',
        body: form({ file }),
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, { id }) => ['GeoSchemes', { type: 'GeoSheetSnaps', id }],
    }),
    /** Реестр РД объекта ИС (ПТО) — документы с листами для шага 1. */
    getGeoRdDocuments: b.query<GeoRdDocuments, string>({
      query: (id) => `${BASE}/schemes/${id}/rd/documents`,
      keepUnusedDataFor: 60,
    }),
    /** Лист ИС из реестра РД: файл редакции копируется в ИС, ссылка остаётся. */
    takeGeoSheetFromRd: b.mutation<
      GeoScheme,
      { id: string; revision_id: string; filename: string }
    >({
      query: ({ id, ...body }) => ({ url: `${BASE}/schemes/${id}/sheet/rd`, method: 'POST', body }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, { id }) => ['GeoSchemes', { type: 'GeoSheetSnaps', id }],
    }),
    /** Лист ИС — на действующую редакцию из реестра РД. */
    refreshGeoSheetRd: b.mutation<GeoScheme, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/sheet/rd/refresh`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, id) => ['GeoSchemes', { type: 'GeoSheetSnaps', id }],
    }),
    setGeoSheetPage: b.mutation<GeoScheme, { id: string; page: number }>({
      query: ({ id, page }) => ({
        url: `${BASE}/schemes/${id}/sheet`,
        method: 'PATCH',
        body: { page },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, { id }) => ['GeoSchemes', { type: 'GeoSheetSnaps', id }],
    }),
    deleteGeoSheet: b.mutation<GeoScheme, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/sheet`, method: 'DELETE' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, id) => ['GeoSchemes', { type: 'GeoSheetSnaps', id }],
    }),
    /**
     * Точки прилипания из векторной графики листа. Кеш — по ИС (тег сбрасывают
     * загрузка, смена страницы и удаление листа); `fileId`/`page` в ключе — чтобы
     * лист, сменённый в другой вкладке, не показал чужие точки. Нет листа — 404.
     */
    getGeoSheetSnaps: b.query<GeoSnapsResponse, { id: string; fileId: string; page: number }>({
      query: ({ id }) => `${BASE}/schemes/${id}/sheet/snaps`,
      providesTags: (_r, _e, { id }) => [{ type: 'GeoSheetSnaps', id }],
    }),
    /** Оси листа с марками и их пересечения; `fileId`/`page` — ключ кеша. */
    getGeoSheetAxes: b.query<GeoSheetAxes, { id: string; fileId: string; page: number }>({
      query: ({ id }) => `${BASE}/schemes/${id}/sheet/axes`,
      keepUnusedDataFor: 300,
    }),
    /**
     * Привязка листа по осям: пересечения осей с марками на листе ↔ X, Y из
     * разбивочного плана; сервер уравнивает, отбрасывает опечатки и пишет
     * контрольные точки `auto:<пересечение>`. 409/400 — с `message`.
     */
    bindGeoSheetByAxes: b.mutation<GeoScheme, { id: string } & GeoAxesBindBody>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/schemes/${id}/binding/axes`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, { id, grid }) =>
        grid ? ['GeoSchemes', { type: 'GeoAxisGrids', id }] : ['GeoSchemes'],
    }),
    /** Выноски «X=…, Y=…» листа; `fileId`/`page` — ключ кеша. */
    getGeoSheetCoords: b.query<GeoSheetCoords, { id: string; fileId: string; page: number }>({
      query: ({ id }) => `${BASE}/schemes/${id}/sheet/coords`,
      keepUnusedDataFor: 300,
    }),
    /** Узлы изометрий комплекта с координатами (центр для привязки листа одной конструкции). */
    getGeoIsoNodes: b.query<GeoIsoNodes, string>({
      query: (id) => `${BASE}/schemes/${id}/iso-nodes`,
      keepUnusedDataFor: 300,
    }),
    /** Привязка листа по выноскам координат на нём. 409/400 — с `message`. */
    bindGeoSheetByCoords: b.mutation<GeoScheme, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/binding/coords`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Лист одной конструкции: план, масштаб по размерам, точка привязки; `fileId`/`page` — ключ кеша. */
    getGeoSheetStructure: b.query<GeoSheetStructure, { id: string; fileId: string; page: number }>({
      query: ({ id }) => `${BASE}/schemes/${id}/sheet/structure`,
      keepUnusedDataFor: 300,
    }),
    /**
     * Привязка листа одной конструкции по её положению на площадке: точка привязки,
     * масштаб плана, поворот (как оси объекта / угол; четверть — по съёмке). 409/400 — с `message`.
     */
    bindGeoSheetByStructure: b.mutation<GeoScheme, { id: string } & GeoStructureBindBody>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/schemes/${id}/binding/structure`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Оси объекта ИС — сетки зданий с координатами пересечений. */
    getGeoAxisGrids: b.query<GeoAxisGrids, string>({
      query: (id) => `${BASE}/schemes/${id}/axis-grids`,
      providesTags: (_r, _e, id) => [{ type: 'GeoAxisGrids', id }],
    }),
    /** Привязка листа по сетке осей объекта — одной кнопкой. 409/400 — с `message`. */
    bindGeoSheetByGrid: b.mutation<GeoScheme, { id: string; grid_id: string }>({
      query: ({ id, grid_id }) => ({
        url: `${BASE}/schemes/${id}/binding/grid`,
        method: 'POST',
        body: { grid_id },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Забыть сетку осей здания объекта; ответ — оставшиеся оси объекта. */
    deleteGeoAxisGrid: b.mutation<GeoAxisGrids, { id: string; gridId: string }>({
      query: ({ id, gridId }) => ({
        url: `${BASE}/schemes/${id}/axis-grids/${gridId}`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          void dispatch(geodesyApi.util.upsertQueryData('getGeoAxisGrids', id, data));
        } catch {
          /* ошибку показывает панель через unwrap() */
        }
      },
    }),
    /**
     * Автопривязка листа по номерам: сервер находит подписанные элементы листа,
     * сопоставляет с каталогом, уравнивает, отбрасывает выбросы и записывает
     * контрольные точки `auto:<номер>` и `binding.result`. 409/400 — с `message`.
     */
    autoBindGeoSheet: b.mutation<GeoScheme, { id: string } & GeoAutoBindBody>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/schemes/${id}/binding/auto`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** PNG превью страницы листа. `fileId`/`page` в аргументе — только ключ кеша:
     *  сменили лист или страницу — превью перечитается. */
    getGeoSheetPreview: b.query<Blob, { id: string; fileId: string; page: number }>({
      query: ({ id }) => ({
        url: `${BASE}/schemes/${id}/sheet/preview`,
        cache: 'no-store',
        responseHandler: blobOrError,
      }),
      keepUnusedDataFor: 60,
    }),

    /* ── Листы комплекта и сборка из листов (ТЗ AI-GEO-2026 v2.1) ─────── */

    /** Листы комплекта с диска (1..N) — чертёж марки, лист анкерных блоков. */
    addGeoSheets: b.mutation<GeoScheme, { id: string; files: File[] }>({
      query: ({ id, files }) => {
        const fd = new FormData();
        for (const f of files) fd.append('files', f);
        return { url: `${BASE}/schemes/${id}/sheets`, method: 'POST', body: fd };
      },
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Листы комплекта из реестра РД ПТО: файлы редакции копируются в ИС. */
    addGeoSheetsFromRd: b.mutation<
      GeoScheme,
      { id: string; items: { revision_id: string; filename: string }[] }
    >({
      query: ({ id, items }) => ({
        url: `${BASE}/schemes/${id}/sheets`,
        method: 'POST',
        body: { items },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Страница листа комплекта и роль листа (у основного — только роль). */
    patchGeoSheet: b.mutation<GeoScheme, { id: string; fileId: string } & GeoSheetPatchBody>({
      query: ({ id, fileId, ...body }) => ({
        url: `${BASE}/schemes/${id}/sheets/${fileId}`,
        method: 'PATCH',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    deleteGeoExtraSheet: b.mutation<GeoScheme, { id: string; fileId: string }>({
      query: ({ id, fileId }) => ({
        url: `${BASE}/schemes/${id}/sheets/${fileId}`,
        method: 'DELETE',
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /**
     * Собрать конструкцию из листов комплекта: ярусы марки и болты с отметками
     * разреза — проектные элементы, композитная подложка — подложка чертежа.
     * Без привязки основного листа — 409; сборка не нашла марок — 400.
     */
    assembleGeoDesign: b.mutation<GeoScheme, { id: string } & GeoAssembleBody>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/schemes/${id}/design/assemble`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Композитная подложка последней сборки (PDF). Не кешируется: сборку повторяют. */
    getGeoCompositeUnderlay: b.query<Blob, string>({
      query: (id) => ({
        url: `${BASE}/schemes/${id}/design/composite`,
        cache: 'no-store',
        responseHandler: blobOrError,
      }),
      keepUnusedDataFor: 0,
    }),

    /* ── Проектные элементы и съёмка ────────────────────────────────────── */

    importGeoDesign: b.mutation<
      GeoScheme,
      { id: string; file: File; column_order: GeoColumnOrder }
    >({
      query: ({ id, file, column_order }) => ({
        url: `${BASE}/schemes/${id}/design/import`,
        method: 'POST',
        body: form({ file, column_order }),
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Извлечь элементы из листа. Без привязки листа backend отвечает 409. */
    extractGeoDesign: b.mutation<GeoScheme, { id: string } & GeoExtractBody>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/schemes/${id}/design/extract`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    putGeoDesign: b.mutation<GeoScheme, { id: string; elements: GeoDesignElement[] }>({
      query: ({ id, elements }) => ({
        url: `${BASE}/schemes/${id}/design`,
        method: 'PUT',
        body: { elements },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    uploadGeoSurvey: b.mutation<
      GeoScheme,
      {
        id: string;
        file: File;
        column_order: GeoColumnOrder;
        mode: GeoSurveyUploadMode;
        assist: GeoSurveyAssist;
      }
    >({
      query: ({ id, file, column_order, mode, assist }) => ({
        url: `${BASE}/schemes/${id}/survey`,
        method: 'POST',
        body: form({ file, column_order, mode, assist }),
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Роли точек по кодам: роли переназначаются, отчёт отклонений пересчитывается. */
    putGeoSurveyCodes: b.mutation<GeoScheme, { id: string; rules: GeoCodeRuleInput[] }>({
      query: ({ id, rules }) => ({
        url: `${BASE}/schemes/${id}/survey/codes`,
        method: 'PUT',
        body: { rules },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Ручная правка точек съёмки — только с причиной (пишется в журнал правок). */
    putGeoSurvey: b.mutation<GeoScheme, { id: string; points: GeoSurveyPoint[]; reason: string }>({
      query: ({ id, points, reason }) => ({
        url: `${BASE}/schemes/${id}/survey`,
        method: 'PUT',
        body: { points, reason },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),

    /* ── Отклонения ─────────────────────────────────────────────────────── */

    computeGeoDeviations: b.mutation<GeoScheme, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/compute`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    patchGeoDeviations: b.mutation<GeoScheme, { id: string; overrides: GeoOverride[] }>({
      query: ({ id, overrides }) => ({
        url: `${BASE}/schemes/${id}/deviations`,
        method: 'PATCH',
        body: { overrides },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),

    /* ── Чертёж и версии ────────────────────────────────────────────────── */

    /** Черновик листа PNG без сохранения версии (шаги 4–5). */
    previewGeoScheme: b.mutation<Blob, string>({
      query: (id) => ({
        url: `${BASE}/schemes/${id}/preview`,
        method: 'POST',
        cache: 'no-store',
        responseHandler: blobOrError,
      }),
    }),
    generateGeoScheme: b.mutation<GeoScheme, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/generate`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Исправленный в CAD файл (DXF/PDF) — новая версия с `source = uploaded`. */
    uploadGeoVersion: b.mutation<GeoScheme, { id: string; file: File; comment: string }>({
      query: ({ id, file, comment }) => ({
        url: `${BASE}/schemes/${id}/versions/upload`,
        method: 'POST',
        body: form({ file, comment }),
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes'],
    }),
    /** Файл версии. Версии неизменяемы — кешировать безопасно. */
    getGeoVersionFile: b.query<Blob, { id: string; version: number; kind: GeoVersionFileKind }>({
      query: ({ id, version, kind }) => ({
        url: `${BASE}/schemes/${id}/versions/${version}/${kind}`,
        responseHandler: blobOrError,
      }),
      keepUnusedDataFor: 120,
    }),
    /**
     * Оцифровка листа для выноса в натуру — проектные элементы в координатах
     * площадки: DXF для AutoCAD, каталог TXT для прибора, CSV для Excel. Не
     * кешируется: элементы меняются.
     */
    getGeoDesignExport: b.query<Blob, { id: string; format: GeoDesignExportFormat }>({
      query: ({ id, format }) => ({
        url: `${BASE}/schemes/${id}/design/export?format=${format}`,
        cache: 'no-store',
        responseHandler: blobOrError,
      }),
      keepUnusedDataFor: 0,
    }),

    /* ── Статусы, журнал, резюме ────────────────────────────────────────── */

    /** Переход статуса. `submit` закрывает связанную заявку — её кеш тоже сбрасываем. */
    transitionGeoScheme: b.mutation<GeoScheme, { id: string; action: GeoAction; reason?: string }>({
      query: ({ id, ...body }) => ({
        url: `${BASE}/schemes/${id}/transition`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
      invalidatesTags: ['GeoSchemes', 'Requests'],
    }),
    commentGeoScheme: b.mutation<GeoScheme, { id: string; text: string }>({
      query: ({ id, text }) => ({
        url: `${BASE}/schemes/${id}/comments`,
        method: 'POST',
        body: { text },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeScheme(dispatch, queryFulfilled),
    }),
    /** LLM-пересказ готового отчёта отклонений для ПТО (числа — из кода). */
    summarizeGeoScheme: b.mutation<{ summary: string }, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/summary`, method: 'POST' }),
    }),

    /* ── ИИ-помощник: предложения без сохранения ────────────────────────── */

    /** Реквизиты штампа и чертёж-основание по листу РД (штамп — программа, текст — ИИ). */
    assistGeoStamp: b.mutation<GeoStampAssist, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/assist/stamp`, method: 'POST' }),
    }),
    /** Формулировки примечаний; предложения с числами не из расчёта программа отбрасывает. */
    assistGeoNotes: b.mutation<GeoNotesAssist, string>({
      query: (id) => ({ url: `${BASE}/schemes/${id}/assist/notes`, method: 'POST' }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetGeoInfoQuery,
  useGetGeoObjectsQuery,
  useGetGeoTolerancesQuery,
  useGetGeoStatsQuery,
  useListGeoSchemesQuery,
  useCreateGeoSchemeMutation,
  useCreateGeoSchemeFromRequestMutation,
  useGetGeoSchemeQuery,
  useUpdateGeoSchemeMutation,
  useDeleteGeoSchemeMutation,
  useResetGeoLayoutMutation,
  useUploadGeoSheetMutation,
  useSetGeoSheetPageMutation,
  useDeleteGeoSheetMutation,
  useGetGeoSheetPreviewQuery,
  useGetGeoSheetSnapsQuery,
  useAutoBindGeoSheetMutation,
  useGetGeoSheetAxesQuery,
  useBindGeoSheetByAxesMutation,
  useGetGeoAxisGridsQuery,
  useGetGeoSheetCoordsQuery,
  useGetGeoRdDocumentsQuery,
  useTakeGeoSheetFromRdMutation,
  useRefreshGeoSheetRdMutation,
  useBindGeoSheetByCoordsMutation,
  useBindGeoSheetByGridMutation,
  useGetGeoSheetStructureQuery,
  useGetGeoIsoNodesQuery,
  useBindGeoSheetByStructureMutation,
  useDeleteGeoAxisGridMutation,
  useAddGeoSheetsMutation,
  useAddGeoSheetsFromRdMutation,
  usePatchGeoSheetMutation,
  useDeleteGeoExtraSheetMutation,
  useAssembleGeoDesignMutation,
  useLazyGetGeoCompositeUnderlayQuery,
  useImportGeoDesignMutation,
  useExtractGeoDesignMutation,
  usePutGeoDesignMutation,
  useUploadGeoSurveyMutation,
  usePutGeoSurveyMutation,
  usePutGeoSurveyCodesMutation,
  useComputeGeoDeviationsMutation,
  usePatchGeoDeviationsMutation,
  usePreviewGeoSchemeMutation,
  useGenerateGeoSchemeMutation,
  useUploadGeoVersionMutation,
  useGetGeoVersionFileQuery,
  useLazyGetGeoVersionFileQuery,
  useLazyGetGeoDesignExportQuery,
  useTransitionGeoSchemeMutation,
  useCommentGeoSchemeMutation,
  useSummarizeGeoSchemeMutation,
  useAssistGeoStampMutation,
  useAssistGeoNotesMutation,
} = geodesyApi;
